/**
 * Componente de Cards de Áreas para Dashboard Administrativo
 * 
 * Este componente exibe uma visualização hierárquica de áreas da empresa,
 * permitindo ao administrador navegar por:
 * 1. Áreas (com paginação - 6 por página)
 * 2. Projetos por área selecionada
 * 3. Demandas por projeto selecionado
 * 4. OS Ativas por demanda/projeto selecionado
 * 
 * @author Equipe GestaoeCRM
 * @date 11/10/2025
 * @version 1.0.0
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Building2, 
  FolderKanban, 
  ClipboardList, 
  ChevronRight, 
  ChevronLeft,
  AlertTriangle,
  Users,
  Clock,
  Briefcase,
  FileText
} from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import { osApi } from '@/services/api';

// ============================================================================
// INTERFACES E TIPOS
// ============================================================================

interface AreaStats {
  activeOSCount: number;
  totalHours: number;
  projectsCount: number;
  demandsCount: number;
  employeesCount: number;
}

interface Area {
  id: string;
  name: string;
  description: string | null;
  stats: AreaStats;
}

interface UserWithoutArea {
  id: string;
  name: string;
  email: string;
  osCount: number;
  sessionsCount: number;
}

interface AreasData {
  areas: Area[];
  usersWithoutArea: UserWithoutArea[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalAreas: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

interface ProjectStats {
  activeOSCount: number;
  totalHours: number;
  demandsCount: number;
  employeesCount: number;
}

interface Project {
  id: string;
  name: string;
  description: string | null;
  status: string;
  client: {
    id: string;
    name: string;
  };
  stats: ProjectStats;
}

interface DemandStats {
  activeOSCount: number;
  totalHours: number;
  employeesCount: number;
  employees: Array<{
    id: string;
    name: string;
  }>;
}

interface Demand {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  assignee: {
    id: string;
    name: string;
  } | null;
  stats: DemandStats;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function AdminAreaCards() {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [viewMode, setViewMode] = useState<'projects' | 'demands-without-project'>('projects');
  const itemsPerPage = 3;

  // ========================================================================
  // QUERIES
  // ========================================================================

  // Buscar todas as áreas (sem paginação do servidor)
  const { data: areasData, isLoading: isLoadingAreas } = useQuery<{ success: boolean; data: AreasData }>({
    queryKey: ['admin-areas-stats'],
    queryFn: () => osApi.getAreaStats(1), // Buscar primeira página com todos os dados
    refetchInterval: 60 * 1000, // Atualizar a cada minuto
  });

  // Buscar projetos da área selecionada
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['admin-area-projects', selectedArea?.id],
    queryFn: () => osApi.getProjectsByArea(selectedArea!.id),
    enabled: !!selectedArea,
  });

  // Buscar demandas do projeto selecionado
  const { data: demandsData, isLoading: isLoadingDemands } = useQuery({
    queryKey: ['admin-project-demands', selectedProject?.id],
    queryFn: () => osApi.getDemandsByProject(selectedProject!.id),
    enabled: !!selectedProject,
  });

  // Buscar demandas sem projeto da área selecionada
  const { data: demandsWithoutProjectData, isLoading: isLoadingDemandsWithoutProject } = useQuery({
    queryKey: ['admin-area-demands-without-project', selectedArea?.id],
    queryFn: () => osApi.getDemandsWithoutProjectByArea(selectedArea!.id),
    enabled: !!selectedArea,
  });

  // ========================================================================
  // HANDLERS
  // ========================================================================

  const handleAreaClick = (area: Area) => {
    setSelectedArea(area);
    setSelectedProject(null); // Reset projeto ao selecionar área
    setViewMode('projects'); // Reset para visualizar projetos por padrão
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
  };

  const handleBackToAreas = () => {
    setSelectedArea(null);
    setSelectedProject(null);
    setViewMode('projects');
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
  };

  const handleToggleViewMode = () => {
    setViewMode(viewMode === 'projects' ? 'demands-without-project' : 'projects');
  };

  // ========================================================================
  // RENDERIZAÇÃO CONDICIONAL
  // ========================================================================

  // Loading
  if (isLoadingAreas) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Sem dados - a resposta vem como areasData.data.data devido à estrutura do axios
  if (!areasData?.data?.data) {
    return (
      <div className="text-center py-12 text-text-muted">
        <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Nenhuma área cadastrada</p>
      </div>
    );
  }

  const { areas = [], usersWithoutArea = [] } = areasData.data.data || { areas: [], usersWithoutArea: [] };
  
  // Calcular paginação local
  const totalPages = Math.ceil(areas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAreas = areas.slice(startIndex, endIndex);

  // ========================================================================
  // NÍVEL 3 - DEMANDAS DO PROJETO SELECIONADO
  // ========================================================================
  if (selectedProject && demandsData?.data?.data) {
    const { project, demands = [] } = demandsData.data.data;

    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-text-muted">
          <button
            onClick={handleBackToAreas}
            className="hover:text-secondary transition-colors"
          >
            Áreas
          </button>
          <ChevronRight className="h-4 w-4" />
          <button
            onClick={handleBackToProjects}
            className="hover:text-secondary transition-colors"
          >
            {selectedArea?.name}
          </button>
          <ChevronRight className="h-4 w-4" />
          <span className="text-text-primary font-medium">{project.name}</span>
        </div>

        {/* Header do Projeto */}
        <div className="card">
          <div className="card-header">
            <div>
              <h3 className="card-title">{project.name}</h3>
              <p className="card-description">{project.client.name}</p>
            </div>
          </div>
        </div>

        {/* Grid de Demandas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoadingDemands ? (
            <div className="col-span-full flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : demands.length === 0 ? (
            <div className="col-span-full text-center py-12 text-text-muted">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma demanda encontrada para este projeto</p>
            </div>
          ) : (
            demands.map((demand: Demand) => (
              <div
                key={demand.id}
                className="card hover:shadow-glow transition-all duration-300 cursor-pointer"
              >
                <div className="card-content space-y-4">
                  {/* Cabeçalho da Demanda */}
                  <div>
                    <h4 className="font-semibold text-text-primary truncate">
                      {demand.title}
                    </h4>
                    {demand.description && (
                      <p className="text-xs text-text-muted mt-1 line-clamp-2">
                        {demand.description}
                      </p>
                    )}
                  </div>

                  {/* Status e Prioridade */}
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      demand.status === 'CONCLUIDA' ? 'bg-green-100 text-green-800' :
                      demand.status === 'EM_ANDAMENTO' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {demand.status}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      demand.priority === 'CRITICA' ? 'bg-red-100 text-red-800' :
                      demand.priority === 'ALTA' ? 'bg-orange-100 text-orange-800' :
                      demand.priority === 'MEDIA' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {demand.priority}
                    </span>
                  </div>

                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-glass">
                    <div>
                      <div className="text-xs text-text-muted">OS Ativas</div>
                      <div className="text-lg font-bold text-text-primary">
                        {demand.stats.activeOSCount}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-text-muted">Horas</div>
                      <div className="text-lg font-bold text-text-primary">
                        {demand.stats.totalHours.toFixed(1)}h
                      </div>
                    </div>
                  </div>

                  {/* Responsável */}
                  {demand.assignee && (
                    <div className="flex items-center space-x-2 pt-2 border-t border-border-glass">
                      <Users className="h-3 w-3 text-text-muted" />
                      <span className="text-xs text-text-muted">
                        {demand.assignee.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ========================================================================
  // NÍVEL 2 - DEMANDAS SEM PROJETO DA ÁREA SELECIONADA
  // ========================================================================
  if (selectedArea && viewMode === 'demands-without-project' && demandsWithoutProjectData?.data?.data) {
    const { demands = [] } = demandsWithoutProjectData.data.data;

    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-text-muted">
          <button
            onClick={handleBackToAreas}
            className="hover:text-secondary transition-colors"
          >
            Áreas
          </button>
          <ChevronRight className="h-4 w-4" />
          <span className="text-text-primary font-medium">{selectedArea.name}</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-text-primary font-medium">Demandas sem Projeto</span>
        </div>

        {/* Header da Área */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="card-title">Demandas sem Projeto - {selectedArea.name}</h3>
                <p className="card-description">
                  {selectedArea.stats.employeesCount} funcionários • {' '}
                  {selectedArea.stats.activeOSCount} OS ativas • {' '}
                  {selectedArea.stats.totalHours.toFixed(1)}h trabalhadas
                </p>
              </div>
              <button
                onClick={handleToggleViewMode}
                className="btn-secondary flex items-center space-x-2"
              >
                <Briefcase className="h-4 w-4" />
                <span>Ver Projetos</span>
              </button>
            </div>
          </div>
        </div>

        {/* Grid de Demandas sem Projeto */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoadingDemandsWithoutProject ? (
            <div className="col-span-full flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : demands.length === 0 ? (
            <div className="col-span-full text-center py-12 text-text-muted">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma demanda sem projeto encontrada para esta área</p>
            </div>
          ) : (
            demands.map((demand: Demand) => (
              <div
                key={demand.id}
                className="card hover:shadow-glow transition-all duration-300 cursor-pointer"
              >
                <div className="card-content space-y-4">
                  {/* Cabeçalho da Demanda */}
                  <div>
                    <h4 className="font-semibold text-text-primary truncate">
                      {demand.title}
                    </h4>
                    {demand.description && (
                      <p className="text-xs text-text-muted mt-1 line-clamp-2">
                        {demand.description}
                      </p>
                    )}
                  </div>

                  {/* Status e Prioridade */}
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      demand.status === 'CONCLUIDA' ? 'bg-green-100 text-green-800' :
                      demand.status === 'EM_ANDAMENTO' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {demand.status}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      demand.priority === 'CRITICA' ? 'bg-red-100 text-red-800' :
                      demand.priority === 'ALTA' ? 'bg-orange-100 text-orange-800' :
                      demand.priority === 'MEDIA' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {demand.priority}
                    </span>
                  </div>

                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-glass">
                    <div>
                      <div className="text-xs text-text-muted">OS Ativas</div>
                      <div className="text-lg font-bold text-text-primary">
                        {demand.stats.activeOSCount}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-text-muted">Horas</div>
                      <div className="text-lg font-bold text-text-primary">
                        {demand.stats.totalHours.toFixed(1)}h
                      </div>
                    </div>
                  </div>

                  {/* Responsável */}
                  {demand.assignee && (
                    <div className="flex items-center space-x-2 pt-2 border-t border-border-glass">
                      <Users className="h-3 w-3 text-text-muted" />
                      <span className="text-xs text-text-muted">
                        {demand.assignee.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ========================================================================
  // NÍVEL 2 - PROJETOS DA ÁREA SELECIONADA
  // ========================================================================
  if (selectedArea && viewMode === 'projects' && projectsData?.data?.data) {
    const { projects = [] } = projectsData.data.data;

    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-text-muted">
          <button
            onClick={handleBackToAreas}
            className="hover:text-secondary transition-colors"
          >
            Áreas
          </button>
          <ChevronRight className="h-4 w-4" />
          <span className="text-text-primary font-medium">{selectedArea.name}</span>
        </div>

        {/* Header da Área */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="card-title">{selectedArea.name}</h3>
                <p className="card-description">
                  {selectedArea.stats.employeesCount} funcionários • {' '}
                  {selectedArea.stats.activeOSCount} OS ativas • {' '}
                  {selectedArea.stats.totalHours.toFixed(1)}h trabalhadas
                </p>
              </div>
              <button
                onClick={handleToggleViewMode}
                className="btn-secondary flex items-center space-x-2"
              >
                <ClipboardList className="h-4 w-4" />
                <span>Ver Demandas sem Projeto</span>
              </button>
            </div>
          </div>
        </div>

        {/* Grid de Projetos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoadingProjects ? (
            <div className="col-span-full flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : projects.length === 0 ? (
            <div className="col-span-full text-center py-12 text-text-muted">
              <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum projeto encontrado para esta área</p>
            </div>
          ) : (
            projects.map((project: Project) => (
              <div
                key={project.id}
                onClick={() => handleProjectClick(project)}
                className="card hover:shadow-glow transition-all duration-300 cursor-pointer"
              >
                <div className="card-content space-y-4">
                  {/* Cabeçalho do Projeto */}
                  <div>
                    <h4 className="font-semibold text-text-primary truncate">
                      {project.name}
                    </h4>
                    <p className="text-xs text-text-muted mt-1">
                      {project.client.name}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      project.status === 'DESENVOLVIMENTO' ? 'bg-blue-100 text-blue-800' :
                      project.status === 'PLANEJAMENTO' ? 'bg-yellow-100 text-yellow-800' :
                      project.status === 'TESTE' ? 'bg-purple-100 text-purple-800' :
                      project.status === 'CONCLUIDO' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {project.status}
                    </span>
                  </div>

                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-glass">
                    <div>
                      <div className="text-xs text-text-muted">OS Ativas</div>
                      <div className="text-lg font-bold text-text-primary">
                        {project.stats.activeOSCount}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-text-muted">Horas</div>
                      <div className="text-lg font-bold text-text-primary">
                        {project.stats.totalHours.toFixed(1)}h
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-text-muted">Demandas</div>
                      <div className="text-lg font-bold text-text-primary">
                        {project.stats.demandsCount}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-text-muted">Funcionários</div>
                      <div className="text-lg font-bold text-text-primary">
                        {project.stats.employeesCount}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ========================================================================
  // NÍVEL 1 - ÁREAS (PADRÃO)
  // ========================================================================
  return (
    <div className="space-y-6">
      {/* Alerta de usuários sem área */}
      {usersWithoutArea.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                Atenção: Usuários sem área cadastrada
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p className="mb-2">
                  Os seguintes usuários possuem atividades mas não têm área cadastrada:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  {usersWithoutArea.map((user) => (
                    <li key={user.id}>
                      <strong>{user.name}</strong> - {user.osCount} OS, {user.sessionsCount} sessões
                    </li>
                  ))}
                </ul>
                <p className="mt-2">
                  Por favor, cadastre a área destes usuários na página{' '}
                  <a href="/users" className="font-medium underline hover:text-yellow-900">
                    Usuários
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid de Áreas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentAreas.length === 0 ? (
          <div className="col-span-full text-center py-12 text-text-muted">
            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma área cadastrada</p>
          </div>
        ) : (
          currentAreas.map((area) => (
            <div
              key={area.id}
              onClick={() => handleAreaClick(area)}
              className="card hover:shadow-glow transition-all duration-300 cursor-pointer"
            >
              <div className="card-content space-y-4">
                {/* Cabeçalho da Área */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-text-primary">{area.name}</h4>
                    {area.description && (
                      <p className="text-xs text-text-muted mt-1 line-clamp-2">
                        {area.description}
                      </p>
                    )}
                  </div>
                  <Building2 className="h-5 w-5 text-secondary flex-shrink-0 ml-2" />
                </div>

                {/* Estatísticas */}
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-glass">
                  <div>
                    <div className="text-xs text-text-muted flex items-center">
                      <FolderKanban className="h-3 w-3 mr-1" />
                      OS Ativas
                    </div>
                    <div className="text-lg font-bold text-text-primary">
                      {area.stats.activeOSCount}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-text-muted flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      Horas
                    </div>
                    <div className="text-lg font-bold text-text-primary">
                      {area.stats.totalHours.toFixed(1)}h
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-text-muted flex items-center">
                      <Briefcase className="h-3 w-3 mr-1" />
                      Projetos
                    </div>
                    <div className="text-lg font-bold text-text-primary">
                      {area.stats.projectsCount}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-text-muted flex items-center">
                      <ClipboardList className="h-3 w-3 mr-1" />
                      Demandas
                    </div>
                    <div className="text-lg font-bold text-text-primary">
                      {area.stats.demandsCount}
                    </div>
                  </div>
                </div>

                {/* Número de Funcionários */}
                <div className="flex items-center space-x-2 pt-3 border-t border-border-glass">
                  <Users className="h-4 w-4 text-text-muted" />
                  <span className="text-sm text-text-muted">
                    {area.stats.employeesCount} funcionários
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 px-4 py-3 bg-gray-50 rounded-lg">
          {/* Informações da página */}
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Página {currentPage} de {totalPages} 
              ({areas.length} {areas.length === 1 ? 'área' : 'áreas'})
            </span>
          </div>

          {/* Controles de navegação */}
          <div className="flex items-center space-x-2">
            {/* Botão Anterior */}
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </button>

            {/* Números das páginas */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            {/* Botão Próximo */}
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              Próximo
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

